<template>
	<div v-if="items.length > 0" class="swiper-grid">
		<PromotionSwiperContainer
			:itemCount="items.length"
			:initialSlide="effectiveInitialSlide"
			:persistedSlideIndex="swipeIndex"
			:delay="slideDelay"
			:enableViewportAutoSwipe="true"
			:isPromoted="isPromoted"
			:hideControls="hideControls"
			@slideChange="handleSlideChange"
		>
			<div v-for="(item, index) in items" :key="getItemKey(item, index)" class="swiper-slide arrow-type">
				<slot :item="item" :index="index"></slot>
			</div>
		</PromotionSwiperContainer>
	</div>
</template>

<script setup lang="ts">
import { Offer } from '@/@types/graphql-types';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { computed, ref } from 'vue';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import PromotionSwiperContainer from '@/components/experiments/GridSwiperExp/components/PromotionSwiperContainer.vue';
import { SnowplowContext } from '@/helpers/tracking/providers';
import type { PromotionItem } from '@/components/experiments/GridSwiperExp';

const props = withDefaults(
	defineProps<{
		items: PromotionItem[];
		title: TitleDetail;
		offers: Offer[];
		additionalContexts?: SnowplowContext[];
		slideDelay?: number;
		initialSlide?: number;
		isPromoted?: boolean;
		trackingProperty?: string;
		/** Auto-swipe to next slide when 50% of component is in viewport. Set to false to disable. */
		autoSwipe?: boolean;
		/** Delay in ms before auto-swiping (only when autoSwipe is true) */
		autoSwipeDelay?: number;
		hideControls?: boolean;
	}>(),
	{
		additionalContexts: () => [],
		slideDelay: 5000,
		initialSlide: 0,
		isPromoted: false,
		trackingProperty: 'promotion_swiper',
		autoSwipe: false,
		autoSwipeDelay: 1500,
		hideControls: false,
	}
);

const emit = defineEmits<{
	(
		e: 'slideChange',
		payload: {
			direction: string;
			position: number;
			nonInteraction: boolean;
		}
	): void;
}>();

const swipeIndex = ref(0);

// Auto-swipe configuration: when enabled, swipe to second slide (index 1) after component is 50% visible
const effectiveInitialSlide = computed(() => {
	if (props.autoSwipe && props.items.length > 1) {
		return 1; // Auto-swipe to second slide
	}
	return props.initialSlide;
});

function getItemKey(item: PromotionItem, index: number): string | number {
	return item.id ?? index;
}

function handleSlideChange({
	direction,
	position,
	nonInteraction,
}: {
	direction: string;
	position: number;
	nonInteraction: boolean;
}) {
	const parsedInitialPosition = Number(swipeIndex.value);
	const initialPosition = isNaN(parsedInitialPosition) ? undefined : parsedInitialPosition;

	const category = nonInteraction ? 'automation' : 'userinteraction';
	const action = nonInteraction ? 'auto_swipe' : 'swipe_clicked';

	swipeIndex.value = position;

	TrackingHelper.trackEvent(category, {
		action,
		label: direction,
		nonInteraction,
		value: initialPosition,
		property: props.trackingProperty,
	});

	emit('slideChange', {
		direction,
		position,
		nonInteraction,
	});
}
</script>

<style scoped lang="scss">
.swiper-grid {
	display: flex;
	flex-direction: row;
	min-height: 208px;
	width: 100%;
	overflow: hidden;
	padding: 16px 16px;

	.swiper-slide {
		flex: 0 0 100%;
		min-width: 0;
		width: 100%;
	}
}

// Variant 2: Boxed style with light grey backgrounds
.v2 {
	.swiper-slide {
		background-color: #ffffff0a;
		border-radius: 8px;
		padding: 16px;
	}
}
</style>
